import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Home, RefreshCw, TrendingUp, AlertCircle, Brain, BookOpen, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const ResultsPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [results, setResults] = useState(location.state?.results || null);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    if (!examId && !results) {
      navigate('/dashboard');
      return;
    }
  }, [examId, results, navigate]);

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (!results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium mb-4">No se encontraron resultados</p>
          <Button onClick={() => navigate('/dashboard')}>Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  const scoreColor = results.score >= 70 ? 'emerald' : results.score >= 50 ? 'amber' : 'rose';
  const officialResults = results.results?.filter(r => !r.is_reserve) || [];
  const reserveResults = results.results?.filter(r => r.is_reserve) || [];
  
  // Calculate topic-based performance
  const topicPerformance = {};
  results.results?.forEach(r => {
    if (!r.is_reserve && r.topic_name) {
      if (!topicPerformance[r.topic_name]) {
        topicPerformance[r.topic_name] = { correct: 0, total: 0 };
      }
      topicPerformance[r.topic_name].total++;
      if (r.is_correct) topicPerformance[r.topic_name].correct++;
    }
  });
  
  // Find weak topics (less than 50% correct)
  const weakTopics = Object.entries(topicPerformance)
    .filter(([_, stats]) => stats.total >= 2 && (stats.correct / stats.total) < 0.5)
    .map(([name, stats]) => ({ name, ...stats, percentage: Math.round((stats.correct / stats.total) * 100) }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Score Header */}
          <div className={`bg-gradient-to-r ${
            scoreColor === 'emerald' ? 'from-emerald-600 to-emerald-700' :
            scoreColor === 'amber' ? 'from-amber-500 to-amber-600' :
            'from-rose-600 to-rose-700'
          } text-white p-12 text-center`}>
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-4">
              {results.score?.toFixed(1) || 0}%
            </h1>
            <p className="text-xl opacity-90">
              {results.correct_count || 0} correctas · {results.incorrect_count || 0} incorrectas · {results.blank_count || 0} en blanco
            </p>
            <p className="text-lg opacity-75 mt-2">
              Puntuación OPE: {results.raw_score?.toFixed(2) || 0} / {results.total_official_questions || officialResults.length} puntos
            </p>
            <p className="text-sm opacity-60 mt-1">
              (Puntuación según configuración de tu oposición)
            </p>
          </div>

          <div className="p-8">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-emerald-50 rounded-xl p-6 text-center border border-emerald-200">
                <p className="text-sm text-emerald-700 mb-1">Correctas</p>
                <p className="text-3xl font-extrabold text-emerald-600">{results.correct_count || 0}</p>
                <p className="text-xs text-emerald-600 mt-1">+{results.correct_count || 0} puntos</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-6 text-center border border-rose-200">
                <p className="text-sm text-rose-700 mb-1">Incorrectas</p>
                <p className="text-3xl font-extrabold text-rose-600">{results.incorrect_count || 0}</p>
                <p className="text-xs text-rose-600 mt-1">Penalización aplicada</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 text-center border border-slate-200">
                <p className="text-sm text-slate-700 mb-1">En Blanco</p>
                <p className="text-3xl font-extrabold text-slate-600">{results.blank_count || 0}</p>
                <p className="text-xs text-slate-600 mt-1">0 puntos</p>
              </div>
            </div>

            {/* Improvement Suggestions */}
            {weakTopics.length > 0 && (
              <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <Brain className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-indigo-900">Áreas de Mejora Detectadas</h3>
                    <p className="text-sm text-indigo-700">Tu rendimiento en estos temas necesita refuerzo:</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {weakTopics.map((topic, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-indigo-100">
                      <span className="font-medium text-slate-700">{topic.name}</span>
                      <span className="text-sm text-rose-600 font-bold">{topic.correct}/{topic.total} ({topic.percentage}%)</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-3">
                  <Button 
                    variant="outline"
                    className="flex-1 rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => navigate('/practice-failed')}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Repasar Preguntas Falladas
                  </Button>
                  <Button 
                    className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={() => {
                      // Open tutor chat with context
                      const tutorBtn = document.querySelector('[data-testid="tutor-toggle-btn"]');
                      if (tutorBtn) {
                        tutorBtn.click();
                        toast.info('El Tutor IA está listo para ayudarte con los temas que necesitas reforzar');
                      } else {
                        toast.error('El Tutor IA no está disponible en este momento');
                      }
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Consultar Tutor IA
                  </Button>
                </div>
              </div>
            )}

            {/* Positive Reinforcement */}
            {results.score >= 70 && (
              <div className="mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-emerald-900">¡Excelente trabajo!</h3>
                    <p className="text-sm text-emerald-700">
                      Tu rendimiento está por encima del umbral de aprobado. ¡Sigue así!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reserve Questions Stats */}
            {reserveResults.length > 0 && (
              <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-amber-900 mb-2">
                  Preguntas de Reserva ({officialResults.length + 1}-{officialResults.length + reserveResults.length})
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  Estas preguntas NO cuentan para tu puntuación oficial, pero aquí está tu rendimiento:
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">{results.reserve_stats?.correct || reserveResults.filter(r => r.is_correct).length}</p>
                    <p className="text-xs text-slate-600">Correctas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-rose-600">{results.reserve_stats?.incorrect || reserveResults.filter(r => !r.is_correct && !r.is_blank).length}</p>
                    <p className="text-xs text-slate-600">Incorrectas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-600">{results.reserve_stats?.blank || reserveResults.filter(r => r.is_blank).length}</p>
                    <p className="text-xs text-slate-600">En blanco</p>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Feedback */}
            <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-indigo-600" />
              Retroalimentación Detallada
            </h2>
            
            <p className="text-sm text-slate-600 mb-4">
              Haz clic en cada pregunta para ver los detalles completos y la explicación.
            </p>
            
            {officialResults.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-slate-700 mb-4">
                  Preguntas Oficiales (1-{officialResults.length})
                </h3>
                <div className="space-y-3 mb-8">
                  {officialResults.map((result, index) => (
                    <div
                      key={index}
                      className={`rounded-xl border-2 overflow-hidden transition-all ${
                        result.is_correct
                          ? 'border-emerald-200 bg-emerald-50'
                          : result.is_blank
                          ? 'border-slate-200 bg-slate-50'
                          : 'border-rose-200 bg-rose-50'
                      }`}
                    >
                      {/* Question Header - Always visible */}
                      <div 
                        className="p-4 cursor-pointer flex items-start gap-3"
                        onClick={() => toggleQuestion(index)}
                      >
                        {result.is_correct ? (
                          <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                        ) : result.is_blank ? (
                          <AlertCircle className="w-6 h-6 text-slate-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-6 h-6 text-rose-600 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`font-bold ${
                              result.is_correct ? 'text-emerald-900' : result.is_blank ? 'text-slate-700' : 'text-rose-900'
                            }`}>
                              Pregunta {result.question_index}: {result.is_correct ? 'Correcta ✓' : result.is_blank ? 'En Blanco' : 'Incorrecta ✗'}
                            </p>
                            <span className="text-xs text-slate-500">
                              {expandedQuestions[index] ? 'Ocultar' : 'Ver detalles'}
                            </span>
                          </div>
                          {/* Question text preview */}
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                            {result.question_text || 'Pregunta no disponible'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedQuestions[index] && (
                        <div className="px-4 pb-4 pt-0 border-t border-slate-200 bg-white/50">
                          {/* Full Question */}
                          <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                            <p className="text-sm font-medium text-slate-500 mb-1">Pregunta:</p>
                            <p className="text-slate-900">{result.question_text}</p>
                          </div>
                          
                          {/* Options */}
                          <div className="mb-4 space-y-2">
                            <p className="text-sm font-medium text-slate-500">Opciones:</p>
                            {result.options?.map((opt, optIdx) => (
                              <div 
                                key={optIdx}
                                className={`p-2 rounded-lg text-sm ${
                                  opt.is_correct 
                                    ? 'bg-emerald-100 border border-emerald-300 text-emerald-900 font-medium'
                                    : opt.option_id === result.selected_option_id && !opt.is_correct
                                    ? 'bg-rose-100 border border-rose-300 text-rose-900'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {opt.is_correct && '✓ '}{opt.text}
                                {opt.is_correct && <span className="ml-2 text-xs">(Respuesta correcta)</span>}
                                {opt.option_id === result.selected_option_id && !opt.is_correct && 
                                  <span className="ml-2 text-xs">(Tu respuesta)</span>
                                }
                              </div>
                            ))}
                            {result.is_blank && (
                              <p className="text-sm text-slate-500 italic">No seleccionaste ninguna opción</p>
                            )}
                          </div>
                          
                          {/* AI Feedback */}
                          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                            <p className="text-sm font-medium text-indigo-700 mb-1 flex items-center gap-1">
                              <Brain className="w-4 h-4" /> Explicación IA:
                            </p>
                            <p className="text-sm text-indigo-900">
                              {result.feedback || 'Feedback no disponible'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Reserve Questions */}
            {reserveResults.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-amber-700 mb-4">
                  Preguntas de Reserva ({officialResults.length + 1}-{officialResults.length + reserveResults.length}) - Solo Feedback
                </h3>
                <div className="space-y-3">
                  {reserveResults.map((result, index) => {
                    const globalIndex = officialResults.length + index;
                    return (
                      <div
                        key={globalIndex}
                        className="rounded-xl border-2 border-amber-200 bg-amber-50 overflow-hidden"
                      >
                        <div 
                          className="p-4 cursor-pointer flex items-start gap-3"
                          onClick={() => toggleQuestion(globalIndex)}
                        >
                          {result.is_correct ? (
                            <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-amber-900">
                              Pregunta {result.question_index} (Reserva): {result.is_correct ? 'Correcta' : result.is_blank ? 'En Blanco' : 'Incorrecta'}
                            </p>
                            <p className="text-sm text-amber-700 mt-1 line-clamp-2">
                              {result.question_text || 'Pregunta no disponible'}
                            </p>
                          </div>
                        </div>
                        
                        {expandedQuestions[globalIndex] && (
                          <div className="px-4 pb-4 pt-0 border-t border-amber-200 bg-white/50">
                            <div className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                              <p className="text-sm font-medium text-amber-600 mb-1">Pregunta:</p>
                              <p className="text-slate-900">{result.question_text}</p>
                            </div>
                            <div className="mb-4 space-y-2">
                              {result.options?.map((opt, optIdx) => (
                                <div 
                                  key={optIdx}
                                  className={`p-2 rounded-lg text-sm ${
                                    opt.is_correct 
                                      ? 'bg-emerald-100 border border-emerald-300 text-emerald-900 font-medium'
                                      : opt.option_id === result.selected_option_id
                                      ? 'bg-rose-100 border border-rose-300 text-rose-900'
                                      : 'bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  {opt.is_correct && '✓ '}{opt.text}
                                </div>
                              ))}
                            </div>
                            <div className="p-3 bg-amber-100 rounded-lg">
                              <p className="text-sm text-amber-900">{result.feedback}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1 rounded-full bg-slate-600 hover:bg-slate-700 text-white py-6 text-lg font-bold"
              >
                <Home className="w-5 h-5 mr-2" />
                Volver al Dashboard
              </Button>
              <Button
                onClick={() => {
                  // Detect if this was a failed questions practice exam
                  const wasPracticeFailed = examId?.startsWith('pf_') || results.exam_type === 'practice_failed';
                  navigate(wasPracticeFailed ? '/practice-failed' : '/exam');
                }}
                className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-bold"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Nuevo Examen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
